import { useEffect, useState } from "react";
import { Clock3, MapPin, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Attendance() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const response = await api.get("/hrms/attendance/today");
      setData(response.data);
      setError("");
    } catch {
      setError("You do not have access to the attendance register.");
    }
  };

  useEffect(() => { load(); }, []);

  const punch = async (event_type) => {
    setBusy(true);
    try {
      await api.post("/hrms/attendance/punch", { event_type, method: "web" });
      toast.success(event_type === "in" ? "Punch-in recorded" : "Punch-out recorded");
      await load();
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || "Unable to record attendance.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="hrms-attendance-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">
            Workforce time
          </p>
          <h1 className="mt-1 text-4xl font-bold">Attendance & Shifts</h1>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => punch("out")}
            data-testid="hrms-punch-out-button"
          >
            Punch out
          </Button>
          <Button
            disabled={busy}
            onClick={() => punch("in")}
            data-testid="hrms-punch-in-button"
          >
            Punch in
          </Button>
        </div>
      </div>
      <section className="grid gap-4 lg:grid-cols-3">
        <div className="card-soft p-5">
          <Clock3 className="h-5 w-5 text-primary" />
          <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Today
          </p>
          <p className="metric mt-1 text-2xl font-bold" data-testid="hrms-attendance-date">
            {data?.date || "—"}
          </p>
        </div>
        <div className="card-soft p-5">
          <MapPin className="h-5 w-5 text-primary" />
          <p className="mt-4 text-sm font-semibold">GPS & selfie attendance</p>
          <p className="mt-1 text-sm text-muted-foreground" data-testid="hrms-face-status">
            Disabled until a verified liveness provider and employee consent are configured.
          </p>
        </div>
        <div className="card-soft p-5">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <p className="mt-4 text-sm font-semibold">Immutable event ledger</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Corrections are separate approval records; original punch events remain intact.
          </p>
        </div>
      </section>
      {error && (
        <p className="text-sm text-destructive" data-testid="hrms-attendance-error">
          {error}
        </p>
      )}
      <div className="card-soft overflow-x-auto" data-testid="hrms-attendance-register">
        <table className="min-w-full text-left text-sm">
          <thead
            className={[
              "border-b border-border bg-muted/50 text-xs uppercase",
              "tracking-wider text-muted-foreground",
            ].join(" ")}
          >
            <tr>
              <th className="px-4 py-3">Employee</th>
              <th className="px-4 py-3">Shift</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Worked</th>
              <th className="px-4 py-3">Late</th>
            </tr>
          </thead>
          <tbody>
            {(data?.items || []).map((row) => (
              <tr key={row.employee_id} className="border-b border-border/60 last:border-0">
                <td className="px-4 py-3 font-medium">{row.employee_name}</td>
                <td className="px-4 py-3">{row.shift_code}</td>
                <td className="px-4 py-3 capitalize">{row.status.replace(/_/g, " ")}</td>
                <td className="px-4 py-3">{row.worked_minutes} min</td>
                <td className="px-4 py-3">{row.late_minutes} min</td>
              </tr>
            ))}
            {!data?.items?.length && (
              <tr>
                <td
                  className="px-4 py-8 text-center text-muted-foreground"
                  colSpan="5"
                  data-testid="hrms-attendance-empty"
                >
                  No attendance events have been recorded today.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}